import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import { Navigation } from '@toolpad/core';
import { useTranslation } from 'react-i18next';
import { Badge } from '@mui/material';
import {
  AccountBalance,
  Approval,
  AutoStories,
  BookmarkAdded,
  Dashboard,
  HelpCenter,
  MonetizationOn,
  OndemandVideo,
  Person,
  Quiz,
  Receipt,
  Rule,
  Verified,
  Assessment,
  VideoCall,
  EventAvailable,
  Palette,
  Extension,
  Payments,
} from '@mui/icons-material';
import { SystemRole } from '../helper-types/general/enums/system-role';
import { SystemClaim } from '../helper-types/general/enums/system-claim';

interface NavItem {
  kind?: 'header' | 'divider';
  segment?: string;
  title?: string;
  icon?: React.ReactNode;
  children?: NavItem[];
  requiredRoles?: string[];
}

const getNavigation = (userRoles: string[], isAuthenticated: boolean, isPlatformAdminImpersonation: boolean = false, pendingEnrollmentRequestsCount: number = 0): Navigation => {
  const { t } = useTranslation();

  const hasAccess = (requiredRoles?: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!isAuthenticated) return false;
    return requiredRoles.some((role) => userRoles.includes(role));
  };

  // Sectioned, non-collapsible layout: a `kind: 'header'` label followed by its items as flat
  // top-level entries — no expand/collapse chevron, everything always visible. This also sidesteps
  // the old `children`-group routing gotcha entirely (see the removed comment this replaced):
  // headers carry no `segment` and items are no longer nested, so there's no ancestor path to
  // prefix a child's route with in the first place.
  const navItems: NavItem[] = [
    // Top item — everyone
    {
      segment: '',
      title: t('nav.dashboard'),
      icon: <Dashboard />,
    },

    // Student section — My Courses/My Exams below each carry a "Browse All" tab for the full
    // tenant catalog (see my-courses-page.tsx/my-exams-page.tsx), so there's no separate
    // top-level catalog entry here anymore.
    { kind: 'header', title: t('nav.sections.myLearning') },
    {
      segment: 'my-courses',
      title: t('nav.myCourses'),
      icon: <BookmarkAdded />,
      requiredRoles: [SystemRole.Student],
    },
    {
      segment: 'my-exams',
      title: t('nav.myExams'),
      icon: <Quiz />,
      requiredRoles: [SystemRole.Student],
    },
    {
      segment: 'my-live-rooms',
      title: t('nav.myLiveRooms'),
      icon: <VideoCall />,
      requiredRoles: [SystemRole.Student],
    },
    {
      segment: 'my-profile',
      title: t('nav.myProfile'),
      icon: <Person />,
      requiredRoles: [SystemRole.Student],
    },

    // Admin section: content authoring (async content only — live/attendance split out below)
    { kind: 'header', title: t('nav.sections.content') },
    {
      segment: 'subjects',
      title: t('nav.subjects'),
      icon: <AutoStories />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessSubjectsTab],
    },
    {
      segment: 'courses',
      title: t('nav.courses'),
      icon: <OndemandVideo />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessCoursesTab],
    },
    {
      segment: 'questions-banks',
      title: t('nav.questionsBanks'),
      icon: <AccountBalance />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessQuestionsBanksTab],
    },
    {
      segment: 'questions',
      title: t('nav.questions'),
      icon: <HelpCenter />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessQuestionsTab],
    },
    {
      segment: 'exams',
      title: t('nav.exams'),
      icon: <Rule />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessExamsTab],
    },

    // Admin section: live/real-time operations, split out of Content (different cadence —
    // scheduled sessions and daily attendance, not authored content)
    { kind: 'header', title: t('nav.sections.liveAndAttendance') },
    {
      segment: 'live-rooms',
      title: t('nav.liveRooms'),
      icon: <VideoCall />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessLiveRoomsTab],
    },
    {
      segment: 'attendance',
      title: t('nav.attendance'),
      icon: <EventAvailable />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessAttendanceTab],
    },

    // Admin section: people management
    { kind: 'header', title: t('nav.sections.people') },
    {
      segment: 'student-table',
      title: t('nav.students'),
      icon: <SchoolIcon />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessStudentsTab],
    },
    {
      segment: 'user-table',
      title: t('nav.users'),
      icon: <GroupIcon />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessUsersTab],
    },
    {
      segment: 'role-table',
      title: t('nav.roles'),
      icon: <BadgeIcon />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessRolesTab],
    },

    // Admin section: monetization + insights
    { kind: 'header', title: t('nav.sections.operations') },
    {
      segment: 'pricing-plans',
      title: t('nav.pricingPlans'),
      icon: <MonetizationOn />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessPricingPlansTab],
    },
    {
      segment: 'licenses',
      title: t('nav.licenses'),
      icon: <Verified />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessLicensesTab],
    },
    {
      segment: 'enrollment-requests',
      title: t('nav.enrollmentRequests'),
      icon: pendingEnrollmentRequestsCount > 0
        ? <Badge badgeContent={pendingEnrollmentRequestsCount} color="error" max={99}><Approval /></Badge>
        : <Approval />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessEnrollmentRequestsTab],
    },
    {
      segment: 'student-results',
      title: t('nav.studentResults'),
      icon: <Assessment />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessStudentResultsTab],
    },
    {
      segment: 'financial-reports',
      title: t('nav.financialReports'),
      icon: <Receipt />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessFinancialReportsTab],
    },

    // Admin section: personal profile + tenant-wide configuration. Profile is dropped
    // entirely for a platform admin who has entered this tenant: they have no real User row
    // here (see the User interface's isPlatformAdminImpersonation doc comment in
    // auth-context.tsx), so there is nothing for that page to show. Their own profile lives
    // in epms.platform instead. Branding/Integrations are tenant-scoped data, not tied to a
    // real User row, so they stay visible for an impersonating platform admin.
    { kind: 'header', title: t('nav.sections.settings') },
    ...(isPlatformAdminImpersonation ? [] : [{
      segment: 'profile',
      title: t('nav.profile'),
      icon: <Person />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessUsersTab, SystemClaim.AccessRolesTab, SystemClaim.AccessStudentsTab, SystemClaim.AccessStudentResultsTab],
    }]),
    {
      segment: 'tenant-branding',
      title: t('nav.tenantBranding'),
      icon: <Palette />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessTenantSettingsTab],
    },
    {
      segment: 'tenant-integrations',
      title: t('nav.tenantIntegrations'),
      icon: <Extension />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessTenantSettingsTab],
    },
    {
      segment: 'tenant-payment-channels',
      title: t('nav.tenantPaymentChannels'),
      icon: <Payments />,
      requiredRoles: [SystemRole.Administrator, SystemClaim.AccessTenantSettingsTab],
    },
  ];

  // Drop role-gated items the signed-in user can't see (headers have no requiredRoles, so they
  // always survive this pass), then drop any header immediately followed by nothing visible —
  // another header or the end of the list — same "no orphan section" rule as before, just
  // applied to a flat array instead of nested children.
  const roleFiltered = navItems.filter((item) => item.kind === 'header' || hasAccess(item.requiredRoles));
  const visible = roleFiltered.filter((item, index) => {
    if (item.kind !== 'header') return true;
    const next = roleFiltered[index + 1];
    return !!next && next.kind !== 'header';
  });

  return visible as Navigation;
};

export default getNavigation;
